import { Injectable } from '@angular/core';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { AuthService } from '../core/auth/auth.service';
import { PosthogService } from './posthog.service';

// Helper: hash simple de demo (PROD: mejor hash en backend)
async function sha256(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

@Injectable({ providedIn: 'root' })
export class AnalyticsIdentityService {
  private lastIdentityKey: string | null = null;

  constructor(private auth: AuthService, private posthog: PosthogService) {}

  init(): void {
    this.auth.session$
      .pipe(
        // Solo cuando ya está verificado + tiene TIN seleccionado
        filter(
          (s) =>
            !!s.user && s.isLoggedIn && s.isTwoFaVerified && !!s.selectedTin
        ),
        map((s) => {
          const account = s.user!.accounts.find(
            (a) => a.tin === s.selectedTin
          )!;
          return { session: s, account };
        }),
        // Evita re-identificar si no cambió email/tin
        map(({ session, account }) => ({
          email: session.user!.email,
          tin: account.tin,
          business_name: account.organization,
          plan: account.plan,
        })),
        distinctUntilChanged((a, b) => a.email === b.email && a.tin === b.tin)
      )
      .subscribe(async ({ email, tin, business_name, plan }) => {
        // Hash determinístico (demo)
        const email_hash = await sha256(`demo|${email.toLowerCase().trim()}`);
        const tin_hash = await sha256(`demo|${tin.trim()}`);

        // Ejemplo de datos “dummy” como dijiste
        const vendor_subtype = 'Auto repair';
        const user_role = 'Admin';
        const xpressone_status = plan === 'premium' ? 'enrolled' : 'unenrolled';
        const global_payment_preference = 'ClaimsCard®';

        const identityKey = `${email_hash}:${tin_hash}`;
        if (this.lastIdentityKey === identityKey) return;
        this.lastIdentityKey = identityKey;

        // 1) Identidad global (person properties)
        this.posthog.identify(email_hash, {
          email_hash,
          tin_hash,
          business_name,
          vendor_subtype,
          user_role,
          xpressone_status,
          global_payment_preference,
        });

        // 2) (Opcional) Super props: se adjuntan AUTOMÁTICO a eventos futuros
        // Útil si quieres que “cada acción” lleve estas 7 props sin repetir en cada capture.
        this.posthog.register({
          email_hash,
          tin_hash,
          business_name,
          vendor_subtype,
          user_role,
          xpressone_status,
          global_payment_preference,
          plan, // extra útil en tu prototipo
        });

        // 3) Evento útil para tu funnel de onboarding del prototipo
        this.posthog.capture('session_identified', { plan });
      });
  }
}
